import { type FormatContext, formatNumber } from "@/shared/format";

/**
 * Quanto um arquivo pesa, dito para uma pessoa.
 *
 * Estava dentro de `features/files` e agora mora aqui porque uma segunda
 * superfície passou a mostrar tamanho de arquivo — a bolha da conversa, quando o
 * Ben entrega um anexo — e uma feature nunca importa outra. Duas cópias de quatro
 * linhas é como "1.5 MB" numa tela vira "1,5 MB" na outra.
 *
 * Base 1024 com os prefixos curtos, que é o que o painel já mostrava na tela de
 * Arquivos; mudar a conta aqui muda aquela tela junto, e é essa a intenção.
 *
 * ── POR QUE A ESCADA NÃO PODIA PARAR EM MB ────────────────────────────────
 *
 * Porque ela parava. Um vídeo de 3 GB lia-se "3072.0 MB", que é um número que
 * ninguém consegue pesar de cabeça — e o card de consumo do plano, que fala em
 * gigabytes desde sempre, dizia a mesma coisa em duas unidades diferentes na
 * mesma tela. GB e TB são os dois degraus que faltavam; o TB entra junto porque
 * a alternativa é voltar aqui no dia em que um workspace passar de mil gigas.
 */
const UNITS = ["B", "kB", "MB", "GB", "TB"] as const;

/**
 * ── UMA CASA DECIMAL, NA LÍNGUA DE QUEM LÊ ────────────────────────────────
 *
 * `toFixed(1)` escrevia "611.3 kB" num painel em português, ao lado de "1,5
 * GB" formatado pelo `Intl` na linha de cima (27/09/2026). A casa decimal é da
 * língua do painel, como todo número; sem agrupamento de milhar, porque "1536,0
 * TB" é o degrau que não troca de unidade e "1.536,0" leria-se como decimal.
 */
const ONE_DECIMAL: Intl.NumberFormatOptions = {
	minimumFractionDigits: 1,
	maximumFractionDigits: 1,
	useGrouping: false,
};

export function humanSize(bytes: number, ctx?: FormatContext): string {
	if (!Number.isFinite(bytes) || bytes < 0) return "—";
	if (bytes < 1024) return `${Math.round(bytes)} B`;

	let value = bytes;
	let step = 0;
	// Para no TB mesmo que a conta continue: acima disso o número volta a crescer
	// em vez de trocar de unidade, e "1536.0 TB" ainda é legível.
	while (value >= 1024 && step < UNITS.length - 1) {
		value /= 1024;
		step += 1;
	}
	return `${formatNumber(value, ctx, ONE_DECIMAL)} ${UNITS[step]}`;
}
